import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { TopbarContainer } from '../../containers';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  NamedLink,
} from '../../components';

import css from './LegalFrameworkLayout.module.css';

export const LEGAL_DOCS = [
  { n: 1, key: 'terms', route: 'TermsOfServicePage' },
  { n: 2, key: 'uucover', route: 'UucoverPage' },
  { n: 3, key: 'serviceFees', route: 'ServicesFreePage' },
  { n: 4, key: 'safety', route: 'CommunicationSafetyPage' },
  { n: 5, key: 'cancellation', route: 'CancellationPolicyPage' },
  { n: 6, key: 'forceMajeure', route: 'ForceMajeurePage' },
  { n: 7, key: 'damageProtection', route: 'TermProtectionPage' },
  { n: 8, key: 'brandGuide', route: 'BrandGuidePage' },
  { n: 9, key: 'dataProtection', route: 'ProtectionDataPage' },
  { n: 10, key: 'privacy', route: 'PrivacyPolicyPage' },
  { n: 11, key: 'cookies', route: 'CookiesPolicyPage' },
];

const LegalNavItem = props => {
  const { doc, currentPage, docTitle, docSubtitle, variant } = props;
  const selected = currentPage === doc.route;
  const itemClassName = classNames({
    [css.sideNavItem]: variant === 'sidebar',
    [css.mobileNavItem]: variant === 'mobile',
    [css.selected]: selected,
  });

  return (
    <NamedLink name={doc.route} className={itemClassName}>
      <span className={variant === 'sidebar' ? css.sideNavNum : css.mobileNavNum}>{doc.n}</span>
      <span className={variant === 'sidebar' ? css.sideNavTexts : css.mobileNavTexts}>
        <span className={variant === 'sidebar' ? css.sideNavTitle : css.mobileNavTitle}>
          {docTitle(doc.key)}
        </span>
        {variant === 'sidebar' ? (
          <span className={css.sideNavSub}>{docSubtitle(doc.key)}</span>
        ) : null}
      </span>
      {variant === 'sidebar' ? (
        <span className={css.sideNavChevron} aria-hidden="true">
          ›
        </span>
      ) : null}
    </NamedLink>
  );
};

LegalNavItem.propTypes = {
  doc: PropTypes.shape({
    n: PropTypes.number.isRequired,
    key: PropTypes.string.isRequired,
    route: PropTypes.string.isRequired,
  }).isRequired,
  currentPage: PropTypes.string,
  docTitle: PropTypes.func.isRequired,
  docSubtitle: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['sidebar', 'mobile']).isRequired,
};

LegalNavItem.defaultProps = {
  currentPage: null,
};

const LegalFrameworkLayoutComponent = props => {
  const {
    children,
    contentClassName,
    currentPage,
    intl,
    scrollingDisabled,
    showMobileNav,
    showSidebar,
    title,
  } = props;
  const docTitle = key => intl.formatMessage({ id: `LegalIndexPage.${key}.title` });
  const docSubtitle = key => intl.formatMessage({ id: `LegalIndexPage.${key}.subtitle` });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage={currentPage || 'LegalIndexPage'} />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain className={css.main}>
          <div className={classNames(css.layout, { [css.layoutNoSidebar]: !showSidebar })}>
            {showSidebar ? (
              <aside className={css.sidebar}>
                <span className={css.sidebarKicker}>
                  <FormattedMessage id="LegalIndexPage.sidebarKicker" />
                </span>
                <h2 className={css.sidebarTitle}>
                  <FormattedMessage id="LegalIndexPage.sidebarTitle" />
                </h2>
                <nav className={css.sideNav}>
                  {LEGAL_DOCS.map(doc => (
                    <LegalNavItem
                      key={doc.route}
                      doc={doc}
                      currentPage={currentPage}
                      docTitle={docTitle}
                      docSubtitle={docSubtitle}
                      variant="sidebar"
                    />
                  ))}
                </nav>
              </aside>
            ) : null}

            <section className={classNames(css.content, contentClassName)}>
              {showMobileNav ? (
                <nav className={css.mobileNav} aria-label={intl.formatMessage({ id: 'LegalIndexPage.allDocs' })}>
                  {LEGAL_DOCS.map(doc => (
                    <LegalNavItem
                      key={doc.route}
                      doc={doc}
                      currentPage={currentPage}
                      docTitle={docTitle}
                      docSubtitle={docSubtitle}
                      variant="mobile"
                    />
                  ))}
                </nav>
              ) : null}
              {children}
            </section>
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

LegalFrameworkLayoutComponent.defaultProps = {
  contentClassName: null,
  currentPage: null,
  showMobileNav: false,
  showSidebar: true,
};

LegalFrameworkLayoutComponent.propTypes = {
  children: PropTypes.node.isRequired,
  contentClassName: PropTypes.string,
  currentPage: PropTypes.string,
  intl: intlShape.isRequired,
  scrollingDisabled: PropTypes.bool.isRequired,
  showMobileNav: PropTypes.bool,
  showSidebar: PropTypes.bool,
  title: PropTypes.string.isRequired,
};

export default injectIntl(LegalFrameworkLayoutComponent);
